import React from "react";
import _ from "lodash";
import { injectIntl, IntlShape } from "react-intl";
import { ColumnDefinition, RowDefinition, components } from "griddle-react";
import { compose } from "recompose";

import { CellBadge, CellDateTime, CellTranslatableMap, CellTranslatableMapBadge, CellViewLink } from "@ui/Table/Cells";
import { Table } from "@ui/Table";
import { withLocale, InjectedLocaleProps } from "@localisation/components/withLocale";
import { Delivery } from "../../models";
import { messages } from "../../messages";

interface DeliveriesTableProps {
    deliveries: Delivery[];
}

interface DeliveriesTableState {
    data: Row[];
    expanded: number;
}

interface Row {
    entity: Delivery;
    id: string;
}

// Type the table component
type TypedTable = new () => Table<Row>;
const TypedTable = Table as TypedTable;

type Props = DeliveriesTableProps & { intl: IntlShape } & InjectedLocaleProps;

class DeliveriesTable extends React.Component<Props, DeliveriesTableState> {
    constructor(props: Props) {
        super(props);

        this.state = {
            data: this.getData(props.deliveries),
            expanded: -1,
        };
    }

    public componentWillReceiveProps(nextProps: Props) {
        let expanded = this.state.expanded;
        // If a row is open and it no longer exists, close it.
        if (expanded > -1 &&
            (
                nextProps.deliveries[expanded] == null ||
                this.props.deliveries[expanded].id !== nextProps.deliveries[expanded].id
            )
        ) {
            expanded = -1;
        }
        this.setState({
            expanded,
            data: this.getData(nextProps.deliveries, expanded),
        });
    }

    public getData(deliveries: Delivery[], expanded: number = -1) {
        return _.reduce(deliveries, (result: Row[], delivery: Delivery, key: number) => {
            result.push({entity: delivery, id: delivery.id});
            if (expanded === key) {
                for (let i = 1; i < delivery.statuses.length; i++) {
                    result.push({entity: new Delivery({currentStatus: delivery.statuses[i]}), id: null});
                }
            }
            return result;
        }, [] as Row[]);
    }

    public onClickRow(key: string) {
        let { expanded } = this.state;
        const i = this.getIndexForKey(key);
        if (i > -1) {
            expanded = expanded === -1 || expanded !== i ? i : -1;
            this.setState({
                expanded,
                data: this.getData(this.props.deliveries, expanded),
            });
        }
    }

    public isDeliveryRow(key: string): boolean {
        const { data } = this.state;
        const i = Number(key);
        return data[i] != null && data[i].id != null;
    }

    public isOpenRow(key: string): boolean {
        const { expanded } = this.state;
        return expanded === Number(key);
    }

    public getIndexForKey(key: string): number {
        const { deliveries } = this.props;
        const { data } = this.state;
        const keyNum = Number(key);
        if (this.isDeliveryRow(key)) {
            return _.findIndex(deliveries, ["id", data[keyNum].id]);
        }
        else if (keyNum < data.length) {
            for (let i = keyNum; i >= 0; i--) {
                if (this.isDeliveryRow(String(i))) {
                    return _.findIndex(deliveries, ["id", data[i].id]);
                }
            }
        }
        return -1;
    }

    public render() {
        const { intl, locale } = this.props;
        const { data } = this.state;
        const CellTranslatable = CellTranslatableMap(locale);
        const CellTranslatableBadge = CellTranslatableMapBadge(locale, {
            mapKey: "label",
            idKey: "name",
            color: {
                "selected": "light",
                "scheduled": "light",
                "in-progress": "warning",
                "delivered": "success",
                "error": "danger",
                "cancelled": "light",
                "not-available": "light",
            },
        });
        return (
            <TypedTable
                data={data}
                pageSize={data.length}
                layout="basic"
                size="small"
                RowEnhancer={
                    (Comp) => (props: components.RowProps) => (
                        <Comp {...props}
                            className={
                                (
                                    !this.isDeliveryRow(props.griddleKey)
                                        ? "table-active"
                                        : (
                                            this.isOpenRow(props.griddleKey)
                                                ? "table-dark"
                                                : ""
                                        )
                                ) + (props.className == null ? "" : " " + props.className)
                            }
                            onClick={() => this.onClickRow(props.griddleKey)}
                        />
                    )
                }>
                <RowDefinition>
                    <ColumnDefinition
                        key="album"
                        id="entity.album"
                        title={intl.formatMessage(messages.deliveryColumnAlbum)}
                        sortable={false}
                        customComponent={CellViewLink}
                    />
                    <ColumnDefinition
                        key="type"
                        id="entity.type.label"
                        title={intl.formatMessage(messages.deliveryColumnType)}
                        customComponent={CellTranslatable}
                        sortable={false}
                    />
                    <ColumnDefinition
                        key="dsp"
                        id="entity.dsp.name"
                        title={intl.formatMessage(messages.deliveryColumnDsp)}
                        sortable={false}
                        customComponent={CellBadge}
                    />
                    <ColumnDefinition
                        key="status"
                        id="entity.currentStatus.type"
                        title={intl.formatMessage(messages.deliveryColumnStatus)}
                        customComponent={CellTranslatableBadge}
                        sortable={false}
                    />
                    <ColumnDefinition
                        key="batchId"
                        id="entity.batch.name"
                        title={intl.formatMessage(messages.deliveryColumnBatch)}
                        sortable={false}
                    />
                    <ColumnDefinition
                        key="occurredAt"
                        id="entity.currentStatus.occurredAt"
                        title={intl.formatMessage(messages.statusColumnOccurredAt)}
                        customComponent={CellDateTime}
                        sortable={false}
                    />
                </RowDefinition>
            </TypedTable>
        );
    }
}

const ConnectedDeliveriesTable = compose<Props, DeliveriesTableProps>(
    injectIntl,
    withLocale,
)(DeliveriesTable);

export {
    DeliveriesTable as DeliveriesTablePresenter,
    ConnectedDeliveriesTable as DeliveriesTable,
    Props as DeliveriesTableProps,
    Row as DeliveriesTableRow,
};
